// src/lib/fishiq/fishIQAreaRead.ts
// Canonical FishIQ read:
// rank first, build evidence second, then cap predictions.

import type { FishIQAreaRead } from '../../types';

import {
  applyFishIQEnvironmentWaterContext,
  buildFishIQLocationContext,
  rankFishIQSpeciesForLocation,
} from '../../services/fishIQLocationRanker';

import {
  buildFishIQConfidence,
  buildFishIQEvidenceTrace,
  buildFishIQLivePulse,
} from './fishIQEvidence';

import {
  buildFishIQMovementTags,
  buildFishIQSpeciesPredictions,
  buildFishIQBestWindow,
  buildFishIQTacticalFusion,
} from './fishIQMovementRead';

import { buildFishIQStructureRead } from './fishIQStructureRead';
import { buildFishIQWaterProfile } from './fishIQWaterProfile';

export type BuildFishIQAreaReadInput = {
  lat: number;
  lng?: number;
  lon?: number;
  waterName?: string | null;
  waterType?: string | null;
  waterKind?: string | null;
  regionHint?: string | null;
  source?: string | null;
  species?: any[] | null;
  catchLog?: any[] | null;
  signals?: any[] | null;
  waterFeatures?: any[] | null;
  environment?: any | null;
};

export function buildFishIQAreaRead(
  input: BuildFishIQAreaReadInput,
): FishIQAreaRead {
  const lng = Number(
    input.lng ?? input.lon ?? 0,
  );

  const profile = buildFishIQWaterProfile({
    lat: input.lat,
    lng,
    waterName: input.waterName || null,
    waterType:
      input.waterType ||
      input.waterKind ||
      null,
    waterKind:
      input.waterKind ||
      input.waterType ||
      null,
    regionHint: input.regionHint || null,
    source:
      input.source || 'fishiq-area-read',
  });

  const baseLocationContext =
    buildFishIQLocationContext({
      lat: profile.lat,
      lon: profile.lng,
      waterName: profile.waterName,
      waterType:
        profile.waterType === 'unknown'
          ? input.waterType ||
            input.waterKind ||
            null
          : profile.waterType,
      waterKind:
        input.waterKind ||
        profile.waterKind ||
        profile.waterType,
      regionHint: profile.regionHint,
      season: profile.season,
      timeOfDay: profile.timeOfDay,
      source:
        profile.source ||
        'fishiq-area-read',
    });

  // Water Intel can provide stronger realm evidence than the coarse map fallback.
  // In particular, a NOAA OFS surface-model hit proves the selected anchor is
  // marine even when a broad geographic heuristic originally called it inland.
  const locationContext = applyFishIQEnvironmentWaterContext(
    baseLocationContext,
    input.environment || null,
  );

  const effectiveProfile = {
    ...profile,
    waterType:
      locationContext.waterKind === 'saltwater'
        ? 'saltwater'
        : locationContext.waterKind === 'brackish'
          ? 'brackish'
          : locationContext.waterKind === 'freshwater'
            ? 'freshwater'
            : profile.waterType,
    waterKind: locationContext.waterKind || profile.waterKind,
    waterName: locationContext.waterName || profile.waterName,
    regionHint: locationContext.regionHint || profile.regionHint,
  };

  const rawSpecies = Array.isArray(
    input.species,
  )
    ? input.species
    : [];

  const rankedSpecies = rawSpecies.length
    ? rankFishIQSpeciesForLocation(
        rawSpecies,
        locationContext,
        {
          catchLog: input.catchLog || [],
          signals: input.signals || [],
          environment: input.environment || null,
        },
      )
    : [];

  const baseStructureTags =
    buildFishIQStructureRead({
      profile: effectiveProfile,
      species: rankedSpecies,
      waterFeatures:
        input.waterFeatures || [],
      environment: input.environment || null,
    });

  const environmentStructureTags = Array.isArray(input.environment?.bathymetry?.structureTypes)
    ? input.environment.bathymetry.structureTypes
        .map((value: any) => String(value || '').trim())
        .filter(Boolean)
    : [];

  const structureTags = Array.from(new Set([
    ...(Array.isArray(baseStructureTags) ? baseStructureTags : []),
    ...environmentStructureTags,
  ]));

  const evidence =
    buildFishIQEvidenceTrace({
      profile: effectiveProfile,
      rankedSpecies,
      signals: input.signals || [],
      catchLog: input.catchLog || [],
      environment: input.environment || null,
    });

  const movementTags =
    buildFishIQMovementTags({
      profile: effectiveProfile,
      species: rankedSpecies,
      structureTags,
      evidence,
      environment: input.environment || null,
    });

  const species =
    buildFishIQSpeciesPredictions({
      profile: effectiveProfile,
      species: rankedSpecies,
      structureTags,
      evidence,
      environment: input.environment || null,
    });

  const tacticalFusion = buildFishIQTacticalFusion({
    profile: effectiveProfile,
    species: rankedSpecies,
    structureTags,
    evidence,
    environment: input.environment || null,
  });

  const livePulse =
    buildFishIQLivePulse({
      signals: input.signals || [],
      catchLog: input.catchLog || [],
    });

  const confidence =
    buildFishIQConfidence({
      profile: effectiveProfile,
      signalCount:
        livePulse.signalCount,
      evidence,
    });

  return {
    lat: effectiveProfile.lat,
    lng: effectiveProfile.lng,
    waterType: effectiveProfile.waterType,
    waterName: effectiveProfile.waterName,
    waterKind: effectiveProfile.waterKind,
    regionHint: effectiveProfile.regionHint,
    species,
    structureTags,
    movementTags,
    bestWindow:
      buildFishIQBestWindow(
        effectiveProfile,
        rankedSpecies,
      ),
    confidence,
    confidenceLabel: evidence.label,
    evidence,
    whyThisRead: evidence.why,
    livePulse,
    environmentalContext: input.environment || null,
    fusion: input.environment
      ? {
          confidence: Number(input.environment?.environmentConfidence || 0),
          evidenceStrength: input.environment?.evidenceStrength || 'limited',
          currentAvailable: input.environment?.current?.available === true,
          depthAvailable: input.environment?.bathymetry?.available === true,
          weatherAvailable: Boolean(input.environment?.weather),
          qualitySummary: input.environment?.qualitySummary || null,
        }
      : null,
    tacticalFusion,
    generatedAt: effectiveProfile.generatedAt,
  } as FishIQAreaRead;
}